import styles from './EquipmentCheckout.module.css';

import { useState } from 'react';
import { DateTime } from 'luxon';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';

import Assignment from '@mui/icons-material/Assignment';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';

export const EquipmentCheckout = ({ patrollerId, onSave, equipmentList = [] }) => {
    const [ checkouts, setCheckouts ] = useState(Array.isArray(equipmentList) ? equipmentList : []);
    const [ newCheckout, setNewCheckout ] = useState({
        patrollerId: patrollerId || '',
        rptDate: DateTime.now().toISODate(),
        rptTime: DateTime.now().toFormat('HH:mm'),
        itemname: '',
        checkout: '',
        checkin: '',
        comments: ''
    });
    const [ isAdding, setIsAdding ] = useState(false);
    const [ error, setError ] = useState(null);
    const [ isSaving, setIsSaving ] = useState(false);

    // Common equipment items
    const equipmentItems = [
        'Radio',
        'First Aid Kit',
        'Oxygen Tank',
        'Toboggan',
        'Backboard',
        'Neck Brace',
        'Splint Kit',
        'Emergency Blanket',
        'Patrol Pack',
        'Avalanche Beacon',
        'Probe',
        'Shovel',
        'Rope',
        'Carabiner',
        'Helmet',
        'Other'
    ];

    const handleNewCheckoutChange = (field, value) => {
        setNewCheckout(prev => ({
            ...prev,
            [ field ]: value
        }));
        setError(null);
    };

    const commit = async (nextCheckouts) => {
        setIsSaving(true);
        setError(null);

        try {
            await onSave(nextCheckouts);
            setCheckouts(nextCheckouts);
            return true;
        } catch (err) {
            setError('Failed to save equipment checkouts: ' + err.message);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddCheckout = async () => {
        if (!newCheckout.itemname || !newCheckout.checkout) {
            setError('Item name and checkout time are required');
            return;
        }

        const checkoutToAdd = {
            ...newCheckout,
            id: `checkout_${ Date.now() }`,
            rptDate: new Date(newCheckout.rptDate).getTime(),
            rptTime: DateTime.fromFormat(newCheckout.rptTime, 'HH:mm').toMillis(),
            checkout: DateTime.fromFormat(newCheckout.checkout, 'HH:mm').toMillis(),
            checkin: newCheckout.checkin ? DateTime.fromFormat(newCheckout.checkin, 'HH:mm').toMillis() : null
        };

        const ok = await commit([ ...checkouts, checkoutToAdd ]);

        if (!ok) return;

        setNewCheckout({
            patrollerId: patrollerId || '',
            rptDate: DateTime.now().toISODate(),
            rptTime: DateTime.now().toFormat('HH:mm'),
            itemname: '',
            checkout: '',
            checkin: '',
            comments: ''
        });
        setIsAdding(false);
    };

    const handleCheckIn = async (checkoutId, checkinTime) => {
        if (!checkinTime) return;

        await commit(checkouts.map(checkout =>
            checkout.id === checkoutId
                ? { ...checkout, checkin: DateTime.fromFormat(checkinTime, 'HH:mm').toMillis() }
                : checkout
        ));
    };

    const handleRemoveCheckout = async (checkoutId) => {
        await commit(checkouts.filter(checkout => checkout.id !== checkoutId));
    };

    const getItemStatus = (checkout) => {
        if (checkout.checkin) {
            return { status: 'returned', color: 'success', icon: CheckCircle };
        }
        return { status: 'checked-out', color: 'warning', icon: Warning };
    };

    return (
        <Card className={ styles.checkoutCard }>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={ 1 }>
                        <Assignment />
                        <Typography variant="h6">Equipment Checkout</Typography>
                    </Box>
                }
                action={
                    <Button
                        startIcon={ <Add /> }
                        onClick={ () => setIsAdding(!isAdding) }
                        size="small"
                        disabled={ isSaving }
                    >
                        { isAdding ? 'Cancel' : 'Add Item' }
                    </Button>
                }
            />

            <CardContent>
                { error && (
                    <Alert variant='filled' severity="error" className={ styles.errorAlert }>
                        { error }
                    </Alert>
                ) }

                {/* Add New Checkout Form */ }
                { isAdding && (
                    <Card variant="outlined" className={ styles.addForm }>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                                Add Equipment Checkout
                            </Typography>
                            <Grid container spacing={ 2 }>
                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <FormControl fullWidth>
                                        <InputLabel>Equipment Item</InputLabel>
                                        <Select
                                            value={ newCheckout.itemname }
                                            label="Equipment Item"
                                            onChange={ (e) => handleNewCheckoutChange('itemname', e.target.value) }
                                        >
                                            { equipmentItems.map((item) => (
                                                <MenuItem key={ item } value={ item }>
                                                    { item }
                                                </MenuItem>
                                            )) }
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <TextField
                                        fullWidth
                                        label="Checkout Time"
                                        type="time"
                                        value={ newCheckout.checkout }
                                        onChange={ (e) => handleNewCheckoutChange('checkout', e.target.value) }
                                        InputLabelProps={ { shrink: true } }
                                    />
                                </Grid>

                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <TextField
                                        fullWidth
                                        label="Check-in Time (Optional)"
                                        type="time"
                                        value={ newCheckout.checkin }
                                        onChange={ (e) => handleNewCheckoutChange('checkin', e.target.value) }
                                        InputLabelProps={ { shrink: true } }
                                    />
                                </Grid>

                                <Grid size={ { xs: 12, sm: 6 } }>
                                    <TextField
                                        fullWidth
                                        label="Date"
                                        type="date"
                                        value={ newCheckout.rptDate }
                                        onChange={ (e) => handleNewCheckoutChange('rptDate', e.target.value) }
                                        InputLabelProps={ { shrink: true } }
                                    />
                                </Grid>

                                <Grid size={ { xs: 12 } } >
                                    <TextField
                                        fullWidth
                                        label="Comments"
                                        multiline
                                        rows={ 2 }
                                        value={ newCheckout.comments }
                                        onChange={ (e) => handleNewCheckoutChange('comments', e.target.value) }
                                        placeholder="Any notes about this equipment checkout..."
                                    />
                                </Grid>

                                <Grid size={ { xs: 12 } }>
                                    <Box display="flex" gap={ 1 } justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            startIcon={ <Cancel /> }
                                            onClick={ () => {
                                                setIsAdding(false);
                                                setError(null);
                                            } }
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={ <Add /> }
                                            onClick={ handleAddCheckout }
                                            disabled={ isSaving }
                                        >
                                            { isSaving ? 'Saving...' : 'Add Checkout' }
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ) }

                {/* Equipment Checkout Table */ }
                { checkouts.length > 0 ? (
                    <TableContainer component={ Paper } className={ styles.tableContainer }>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Checkout</TableCell>
                                    <TableCell>Check-in</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Comments</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { checkouts.map((checkout) => {
                                    const statusInfo = getItemStatus(checkout);
                                    return (
                                        <TableRow key={ checkout.id }>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    { checkout.itemname }
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                { DateTime.fromMillis(checkout.checkout).toFormat('HH:mm') }
                                            </TableCell>
                                            <TableCell>
                                                { checkout.checkin ? (
                                                    DateTime.fromMillis(checkout.checkin).toFormat('HH:mm')
                                                ) : (
                                                    <TextField
                                                        type="time"
                                                        size="small"
                                                        onChange={ (e) => handleCheckIn(checkout.id, e.target.value) }
                                                        placeholder="Check-in time"
                                                        disabled={ isSaving }
                                                    />
                                                ) }
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={ <statusInfo.icon /> }
                                                    label={ statusInfo.status }
                                                    color={ statusInfo.color }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" className={ styles.commentsCell }>
                                                    { checkout.comments || '-' }
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={ () => handleRemoveCheckout(checkout.id) }
                                                    color="error"
                                                    disabled={ isSaving }
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box className={ styles.emptyState }>
                        <Typography variant="body1" color="text.secondary">
                            No equipment checked out yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Click &ldquo;Add Item&rdquo; to start tracking equipment.
                        </Typography>
                    </Box>
                ) }
            </CardContent>
        </Card>
    );
};
